import React, { useCallback, useEffect, useState } from "react";
import {
  VisitNoteEntryResponse,
  VisitNoteEntryVersionResponse,
  NoteSection,
  NotesAPI,
  NotesApiError,
} from "../services/visit-notes.service";
import { useRole } from "../hooks/useRole";

// ─── Icons ────────────────────────────────────────────────────────────────────
// Same Feather-style inline-SVG convention as AllergiesPanel.

export const IcNote = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
  </svg>
);

const IcPlus = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const IcPencil = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const IcCheck = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const IcChevronDown = () => (
  <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

// ─── Config ───────────────────────────────────────────────────────────────────

const SECTION_LABEL: Record<NoteSection, string> = {
  subjective: "Subjective",
  objective: "Objective",
  assessment: "Assessment",
  plan: "Plan",
  general: "General",
};

const SECTIONS: NoteSection[] = ["general", "subjective", "objective", "assessment", "plan"];

export function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}, ${d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}`;
}

// ─── Add / edit form ──────────────────────────────────────────────────────────

export interface NoteFormProps {
  initialBody: string;
  initialSection?: NoteSection;
  showSection: boolean;
  showReason: boolean;
  onSave: (body: string, section: NoteSection | undefined, reason: string | undefined) => Promise<void>;
  onCancel: () => void;
  saving: boolean;
  error: string | null;
}

export const NoteForm: React.FC<NoteFormProps> = ({
  initialBody, initialSection, showSection, showReason, onSave, onCancel, saving, error,
}) => {
  const [body, setBody] = useState(initialBody);
  const [section, setSection] = useState<NoteSection | undefined>(initialSection);
  const [reason, setReason] = useState("");

  const fieldBase =
    "w-full text-base sm:text-sm text-slate-700 bg-slate-100 rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-cyan-300 transition-shadow placeholder:text-slate-400";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!body.trim()) return;
    await onSave(body, section, showReason ? (reason.trim() || undefined) : undefined);
  };

  return (
    <form onSubmit={handleSubmit} className="mt-3 flex flex-col gap-2.5 bg-slate-50 rounded-2xl border border-slate-100 p-3">
      {showSection && (
        <select
          value={section ?? "general"}
          onChange={e => setSection(e.target.value as NoteSection)}
          className={fieldBase + " cursor-pointer"}
        >
          {SECTIONS.map(s => (
            <option key={s} value={s}>{SECTION_LABEL[s]}</option>
          ))}
        </select>
      )}

      <textarea
        placeholder="Note text *"
        value={body}
        onChange={e => setBody(e.target.value)}
        required
        rows={3}
        className={fieldBase}
        autoFocus
      />

      {showReason && (
        <input
          type="text"
          placeholder="Reason for edit (optional)"
          value={reason}
          onChange={e => setReason(e.target.value)}
          className={fieldBase}
        />
      )}

      {error && (
        <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">{error}</p>
      )}

      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-1.5 rounded-xl border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-100 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving || !body.trim()}
          className="flex items-center gap-1.5 px-4 py-1.5 rounded-xl bg-cyan-500 hover:bg-cyan-600 text-white text-xs font-semibold transition-colors disabled:opacity-50"
        >
          {saving ? <>Saving…</> : <><IcCheck />Save</>}
        </button>
      </div>
    </form>
  );
};

// ─── History accordion ────────────────────────────────────────────────────────
// No popover primitive exists in this codebase (confirmed against AllergiesPanel/
// AllergyForm) — an inline expandable list is the simplest fit, not a new one.

const HistoryList: React.FC<{ versions: VisitNoteEntryVersionResponse[] }> = ({ versions }) => (
  <div className="mt-2 rounded-xl border border-slate-100 bg-slate-50 divide-y divide-slate-100">
    {versions.slice().reverse().map(v => (
      <div key={v.version_id} className="px-3 py-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] font-semibold text-slate-500 bg-slate-100 rounded-full px-2 py-0.5 leading-none">
            v{v.version_no}
          </span>
          <span className="text-xs font-medium text-slate-700">{v.edited_by_name}</span>
          <span className="text-[10px] font-medium text-blue-600 bg-blue-50 rounded-full px-1.5 py-0.5 leading-none">
            {v.edited_by_role}
          </span>
          <span className="text-[11px] text-slate-400">{formatTimestamp(v.edited_at)}</span>
        </div>
        <p className="text-xs text-slate-600 mt-1 whitespace-pre-wrap">{v.body}</p>
        {v.edit_reason && (
          <p className="text-[11px] text-slate-400 italic mt-0.5">Reason: {v.edit_reason}</p>
        )}
      </div>
    ))}
  </div>
);

// ─── Entry row ────────────────────────────────────────────────────────────────

export interface NoteRowProps {
  entry: VisitNoteEntryResponse;
  onEdit: () => void;
  isEditing: boolean;
  editForm: React.ReactNode;
  historyOpen: boolean;
  historyVersions: VisitNoteEntryVersionResponse[] | null;
  historyLoading: boolean;
  onToggleHistory: () => void;
}

export const NoteRow: React.FC<NoteRowProps> = ({
  entry, onEdit, isEditing, editForm, historyOpen, historyVersions, historyLoading, onToggleHistory,
}) => {
  if (isEditing) return <div className="py-2">{editForm}</div>;

  const edited = entry.current_version_no > 1;

  return (
    <div className="py-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <span className="text-sm font-semibold text-slate-800">{entry.author_name}</span>
          <span className="text-[11px] font-medium text-blue-600 bg-blue-50 rounded-full px-2 py-0.5 leading-none">
            {entry.author_role}
          </span>
          {entry.section && (
            <span className="text-[11px] font-medium text-slate-500 bg-slate-100 rounded-full px-2 py-0.5 leading-none">
              {SECTION_LABEL[entry.section]}
            </span>
          )}
          <span className="text-[11px] text-slate-400">{formatTimestamp(entry.created_at)}</span>
          {edited && (
            <span className="text-[11px] text-slate-400 italic">(edited)</span>
          )}
        </div>
        {entry.can_edit && (
          <button
            onClick={onEdit}
            className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex-shrink-0"
            title="Edit"
          >
            <IcPencil />
          </button>
        )}
      </div>

      <p className="text-sm text-slate-700 mt-1.5 whitespace-pre-wrap">{entry.body}</p>

      {edited && (
        <button
          onClick={onToggleHistory}
          className="mt-1.5 flex items-center gap-1 text-[11px] font-medium text-slate-400 hover:text-slate-600 transition-colors"
        >
          <span className={`transition-transform ${historyOpen ? "rotate-180" : ""}`}><IcChevronDown /></span>
          {historyOpen ? "Hide history" : "View history"}
        </button>
      )}

      {historyOpen && (
        historyLoading
          ? <p className="text-xs text-slate-400 mt-2">Loading history…</p>
          : historyVersions && <HistoryList versions={historyVersions} />
      )}
    </div>
  );
};

// ─── Main panel ───────────────────────────────────────────────────────────────

interface VisitNotesPanelProps {
  visitId: string;
}

const VisitNotesPanel: React.FC<VisitNotesPanelProps> = ({ visitId }) => {
  const { isDoctor, isAdmin, can_prescribe } = useRole();
  // Credential-based, matching the backend's actual require_clinical_credential
  // gate — not role-string-based. Adding an entry is open to any credentialed
  // clinic doctor under both edit policies (the policy only gates edits).
  const canAdd = (isDoctor || isAdmin) && can_prescribe;

  const [entries, setEntries] = useState<VisitNoteEntryResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const [showAddForm, setShowAddForm] = useState(false);
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const [historyOpenId, setHistoryOpenId] = useState<string | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyByEntry, setHistoryByEntry] = useState<Record<string, VisitNoteEntryVersionResponse[]>>({});

  const load = useCallback(async () => {
    if (!visitId) { setLoading(false); return; }
    try {
      const data = await NotesAPI.list(visitId);
      setEntries(data);
      setFetchError(null);
    } catch (e) {
      if (e instanceof NotesApiError && e.statusCode === 404) {
        setEntries([]);
        setFetchError(null);
      } else {
        setFetchError(e instanceof NotesApiError ? e.message : "Failed to load visit notes");
      }
    } finally {
      setLoading(false);
    }
  }, [visitId]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (body: string, section: NoteSection | undefined) => {
    setAddSaving(true);
    setAddError(null);
    try {
      const created = await NotesAPI.create(visitId, { body, section });
      setEntries(prev => [...prev, created]);
      setShowAddForm(false);
    } catch (e) {
      setAddError(e instanceof NotesApiError ? e.message : "Failed to add note");
    } finally {
      setAddSaving(false);
    }
  };

  const handleEdit = async (entryId: string, body: string, _section: NoteSection | undefined, reason: string | undefined) => {
    setEditSaving(true);
    setEditError(null);
    try {
      const updated = await NotesAPI.update(entryId, { body, edit_reason: reason });
      setEntries(prev => prev.map(e => (e.entry_id === entryId ? updated : e)));
      setEditingId(null);
      // The head just changed — drop any stale cached history for this entry.
      setHistoryByEntry(prev => { const { [entryId]: _drop, ...rest } = prev; return rest; });
    } catch (e) {
      setEditError(e instanceof NotesApiError ? e.message : "Failed to save edit");
    } finally {
      setEditSaving(false);
    }
  };

  const handleToggleHistory = async (entryId: string) => {
    if (historyOpenId === entryId) { setHistoryOpenId(null); return; }
    setHistoryOpenId(entryId);
    if (historyByEntry[entryId]) return;
    setHistoryLoading(true);
    try {
      const versions = await NotesAPI.history(entryId);
      setHistoryByEntry(prev => ({ ...prev, [entryId]: versions }));
    } catch {
      /* leave history empty on failure — the toggle can be retried */
    } finally {
      setHistoryLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm px-4 pt-4 pb-3">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center text-blue-500">
            <IcNote />
          </div>
          <h2 className="text-[15px] font-bold text-slate-900">Visit Notes</h2>
          {entries.length > 0 && (
            <span className="text-xs font-semibold text-slate-500 bg-slate-100 rounded-full px-2 py-0.5 leading-none">
              {entries.length}
            </span>
          )}
        </div>
        {canAdd && !showAddForm && (
          <button
            onClick={() => setShowAddForm(true)}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-xl text-white text-sm font-semibold shadow-sm hover:opacity-90 transition-opacity bg-gradient-to-br from-blue-500 to-sky-400"
          >
            <IcPlus /> Add
          </button>
        )}
      </div>

      {loading && <p className="text-xs text-slate-400 text-center py-4">Loading…</p>}
      {!loading && fetchError && <p className="text-xs text-red-500 text-center py-4">{fetchError}</p>}
      {!loading && !fetchError && entries.length === 0 && !showAddForm && (
        <p className="text-xs text-slate-400 text-center py-4">No visit notes yet</p>
      )}

      {!loading && !fetchError && entries.map((entry, i) => (
        <div key={entry.entry_id} className={i < entries.length - 1 || showAddForm ? "border-b border-slate-100" : ""}>
          <NoteRow
            entry={entry}
            onEdit={() => { setEditingId(entry.entry_id); setEditError(null); }}
            isEditing={editingId === entry.entry_id}
            editForm={
              <NoteForm
                initialBody={entry.body}
                showSection={false}
                showReason
                onSave={(body, section, reason) => handleEdit(entry.entry_id, body, section, reason)}
                onCancel={() => { setEditingId(null); setEditError(null); }}
                saving={editSaving}
                error={editError}
              />
            }
            historyOpen={historyOpenId === entry.entry_id}
            historyVersions={historyByEntry[entry.entry_id] ?? null}
            historyLoading={historyLoading && historyOpenId === entry.entry_id}
            onToggleHistory={() => handleToggleHistory(entry.entry_id)}
          />
        </div>
      ))}

      {showAddForm && (
        <NoteForm
          initialBody=""
          showSection
          showReason={false}
          onSave={(body, section) => handleAdd(body, section)}
          onCancel={() => { setShowAddForm(false); setAddError(null); }}
          saving={addSaving}
          error={addError}
        />
      )}
    </div>
  );
};

export default VisitNotesPanel;
